import { nextTick } from 'vue';
import type { ApiResult } from '@/core/either';
import type { ApiError } from '../types/recaptcha';
import { RECAPTCHA_FALLBACK_REASON } from '../constants/recaptcha.constant';

/**
 * Forma de error que consume executeRecaptchaFlow para activar el fallback a V2.
 */
export function createRecaptchaFallbackError(
  reason: string = RECAPTCHA_FALLBACK_REASON
): ApiError {
  return {
    name: 'RecaptchaFallbackError',
    message: reason,
    response: { data: { reason } },
  };
}

/**
 * `createRecaptchaFallbackError` devuelve un objeto plano, así que
 * `instanceof Error` da `false`: la identificación va por su `reason`.
 */
export function isRecaptchaFallbackError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  return (
    (error as ApiError).response?.data?.reason === RECAPTCHA_FALLBACK_REASON
  );
}

/**
 * Traduce un `Left` con reason de recaptcha al throw que `executeRecaptchaFlow`
 * necesita para escalar a V2: los services devuelven `Either` y nunca lanzan,
 * así que sin este puente el `catch` que dispara el fallback no corre nunca.
 *
 * @returns el mismo resultado cuando no hay que escalar
 */
export function throwOnRecaptchaFallback<T>(result: ApiResult<T>): ApiResult<T> {
  if (result.isLeft() && result.value.reason === RECAPTCHA_FALLBACK_REASON) {
    throw createRecaptchaFallbackError(result.value.reason);
  }

  return result;
}

/**
 * Espera a que el contenedor V2 quede pintado tras mostrar la sección (v-show).
 */
export async function waitForRecaptchaContainer(): Promise<void> {
  await nextTick();
  await new Promise<void>((resolve) => {
    let isSettled = false;
    const finish = () => {
      if (isSettled) return;
      isSettled = true;
      resolve();
    };

    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(finish);
    }

    setTimeout(finish, 0);
  });
}
